import axios from 'axios';
import BASE_URL from '../config/BASE_URL';
import { getAttribution } from './tracker';

/**
 * One axios instance for the whole site.
 *
 * Two things happen automatically on every request:
 *  1. Public POSTs get the visitor's attribution (channel, utm, session id)
 *     attached, so the console can tell which channel produced a lead.
 *  2. Admin requests get the bearer token attached, and a 401 clears the
 *     session and bounces to the sign-in screen.
 */
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

export const TOKEN_KEY = 'sp_admin_token';

api.interceptors.request.use((config) => {
  const isAdmin = config.url?.startsWith('/api/admin');

  if (isAdmin) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  }

  // Attach attribution to public writes. FormData needs .append; JSON bodies
  // get merged. Never overwrite a value the caller set explicitly.
  if (['post', 'put', 'patch'].includes(config.method)) {
    const attribution = getAttribution();

    if (config.data instanceof FormData) {
      Object.entries(attribution).forEach(([key, value]) => {
        if (value != null && !config.data.has(key)) config.data.append(key, value);
      });
    } else {
      config.data = { ...attribution, ...(config.data || {}) };
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, code } = error;

    // Shape every failure the same way so callers never dig through axios.
    let message = 'Something went wrong. Please try again.';
    let fields = null;
    let status = response?.status ?? 0;

    if (code === 'ECONNABORTED') {
      message = 'That took too long. Check your connection and try again.';
    } else if (!response) {
      message = 'We could not reach the server. Check your connection and try again.';
    } else {
      message = response.data?.error || message;
      fields = response.data?.fields || null;

      if (status === 401 && error.config?.url?.startsWith('/api/admin')) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('sp_admin_user');
        if (!window.location.pathname.startsWith('/admin/login')) {
          window.location.assign('/admin/login?expired=1');
        }
      }
    }

    return Promise.reject(Object.assign(error, { message, fields, status }));
  }
);

export default api;
